import React, { useState, useEffect } from 'react';
import { Card, Tabs, Button, message, Spin } from 'antd';
import { 
  UserOutlined, 
  SettingOutlined, 
  MonitorOutlined, 
  DatabaseOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import UserManagement from './components/UserManagement';
import SystemConfig from './components/SystemConfig';
import SystemMonitor from './components/SystemMonitor';
import SystemLogs from './components/SystemLogs';
import { adminService } from '../../services/adminService';
import './SystemAdmin.css';

const { TabPane } = Tabs;

interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalSpots: number;
  occupiedSpots: number;
  systemHealth: 'healthy' | 'warning' | 'error';
  uptime: number;
}

const SystemAdmin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);

  useEffect(() => {
    loadSystemStats();
  }, []);

  const loadSystemStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getSystemStats();
      if (response.success && response.data) {
        setSystemStats(response.data);
      }
    } catch (error) {
      console.error('載入系統統計失敗:', error);
      message.error('載入系統統計失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadSystemStats();
    message.success('數據已刷新');
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return '#52c41a';
      case 'warning': return '#faad14';
      case 'error': return '#ff4d4f';
      default: return '#d9d9d9';
    }
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}天 ${hours}小時 ${minutes}分鐘`;
  };

  return (
    <div className="system-admin">
      <div className="admin-header">
        <div className="header-content">
          <h1>
            <SettingOutlined /> 系統管理
          </h1>
          <Button 
            type="primary" 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={loading}
          >
            刷新數據
          </Button>
        </div>
      </div>

      {/* 系統狀態概覽 */}
      <div className="system-overview">
        <div className="overview-cards">
          <Card className="stat-card">
            <div className="stat-content">
              <div className="stat-icon user-icon">
                <UserOutlined />
              </div>
              <div className="stat-info">
                <h3>{systemStats?.totalUsers || 0}</h3>
                <p>總用戶數</p>
                <span className="stat-detail">
                  活躍: {systemStats?.activeUsers || 0}
                </span>
              </div>
            </div>
          </Card>

          <Card className="stat-card">
            <div className="stat-content">
              <div className="stat-icon parking-icon">
                <DatabaseOutlined />
              </div>
              <div className="stat-info">
                <h3>{systemStats?.totalSpots || 0}</h3>
                <p>停車位總數</p>
                <span className="stat-detail">
                  已佔用: {systemStats?.occupiedSpots || 0}
                </span>
              </div>
            </div>
          </Card>

          <Card className="stat-card">
            <div className="stat-content">
              <div className="stat-icon monitor-icon">
                <MonitorOutlined />
              </div>
              <div className="stat-info">
                <h3 style={{ color: getHealthStatusColor(systemStats?.systemHealth || 'healthy') }}>
                  {systemStats?.systemHealth === 'healthy' ? '正常' : 
                   systemStats?.systemHealth === 'warning' ? '警告' : '錯誤'}
                </h3>
                <p>系統狀態</p>
                <span className="stat-detail">
                  運行時間: {systemStats ? formatUptime(systemStats.uptime) : '0分鐘'}
                </span>
              </div>
            </div>
          </Card>

          <Card className="stat-card">
            <div className="stat-content">
              <div className="stat-icon health-icon">
                <ExclamationCircleOutlined />
              </div>
              <div className="stat-info">
                <h3>{systemStats?.occupiedSpots && systemStats?.totalSpots ? 
                  ((systemStats.occupiedSpots / systemStats.totalSpots) * 100).toFixed(1) : '0'}%</h3>
                <p>停車場使用率</p>
                <span className="stat-detail">
                  實時數據
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* 管理功能標籤頁 */}
      <Card className="admin-tabs-card">
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          type="card"
          size="large"
        >
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                用戶管理
              </span>
            } 
            key="users"
          >
            <UserManagement />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <SettingOutlined />
                系統配置
              </span>
            } 
            key="config"
          >
            <SystemConfig />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <MonitorOutlined />
                系統監控
              </span>
            } 
            key="monitor"
          >
            <SystemMonitor />
          </TabPane>

          <TabPane 
            tab={
              <span>
                <DatabaseOutlined />
                系統日誌
              </span>
            } 
            key="logs"
          >
            <SystemLogs />
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default SystemAdmin;